// P05-4-5: Coach Dashboard
// Displays student metrics for verified coaches

import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Trophy, Flame, MessageSquare, Calendar, Loader2, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

interface StudentMetric {
  coach_id: string;
  student_id: string;
  student_name: string | null;
  user_id: string;
  post_streak: number;
  longest_post_streak: number;
  badge_count: number;
  recent_posts: number;
  last_activity_at: string | null;
}

interface DashboardResponse {
  students: StudentMetric[];
}

async function fetchDashboard(): Promise<DashboardResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/coach-dashboard-get`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to fetch dashboard';
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    
    // Provide helpful error message for 403
    if (response.status === 403) {
      errorMessage = 'Only verified coaches can access the dashboard. Please ensure your profile has is_coach=true and verified=true.';
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
}

export default function CoachDashboard() {
  const { data, isLoading, error } = useQuery<DashboardResponse>({
    queryKey: ['coach-dashboard'],
    queryFn: fetchDashboard,
    refetchInterval: 600000, // Refetch every 10 minutes
  });

  if (isLoading) {
    return (
      <div className="min-h-screen pb-20">
        <header className="bg-gradient-to-r from-teal-700 to-emerald-600 px-4 md:px-8 py-8 md:py-12 shadow-lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto"
          >
            <h1 className="text-white mb-2 text-3xl md:text-4xl">Coach Dashboard</h1>
            <p className="text-teal-100">
              Track your students' progress and engagement
            </p>
          </motion.div>
        </header>
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-emerald-600" size={32} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pb-20">
        <header className="bg-gradient-to-r from-teal-700 to-emerald-600 px-4 md:px-8 py-8 md:py-12 shadow-lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto"
          >
            <h1 className="text-white mb-2 text-3xl md:text-4xl">Coach Dashboard</h1>
            <p className="text-teal-100">
              Track your students' progress and engagement
            </p>
          </motion.div>
        </header>
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-destructive font-semibold">Failed to load dashboard</p>
            <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  const students = data?.students || [];

  return (
    <div className="min-h-screen pb-20">
      {/* Header - FIGMA gradient matching Clubhouse */}
      <header className="bg-gradient-to-r from-teal-700 to-emerald-600 px-4 md:px-8 py-8 md:py-12 shadow-lg">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-7xl mx-auto"
        >
          <h1 className="text-white mb-2 text-3xl md:text-4xl">Coach Dashboard</h1>
          <p className="text-teal-100">
            Track your students' progress and engagement
          </p>
        </motion.div>
      </header>

      {/* Content - Animated transitions matching Clubhouse */}
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {students.length === 0 ? (
            <>
              {/* Empty State with Helpful Information */}
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="rounded-full bg-muted p-6 mb-4">
                    <Users className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">No students yet</h3>
                  <p className="text-muted-foreground text-center max-w-md mb-6">
                    Students who follow you will appear here with their activity metrics, streaks, badges, and recent posts.
                  </p>
                  
                  {/* Helpful Tips */}
                  <div className="w-full max-w-2xl space-y-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        How to get students:
                      </h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Share your coach profile link with golfers</li>
                        <li>Post Office Hours content to engage with the community</li>
                        <li>Create challenges to attract participants</li>
                        <li>Appear in the Coach Directory for discovery</li>
                      </ul>
                    </div>
                    
                    <div className="flex gap-4 justify-center">
                      <a 
                        href="/coach-directory" 
                        className="text-sm text-primary hover:underline"
                      >
                        View Coach Directory →
                      </a>
                      <a 
                        href="/profile" 
                        className="text-sm text-primary hover:underline"
                      >
                        Edit Your Profile →
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Placeholder Stats Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Stats</CardTitle>
                  <CardDescription>Your dashboard metrics will appear here once you have students</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-muted-foreground">0</div>
                      <div className="text-sm text-muted-foreground">Total Students</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-muted-foreground">0</div>
                      <div className="text-sm text-muted-foreground">Total Streaks</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-muted-foreground">0</div>
                      <div className="text-sm text-muted-foreground">Total Badges</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-muted-foreground">0</div>
                      <div className="text-sm text-muted-foreground">Recent Posts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-2xl font-bold">{students.length}</div>
                      <div className="text-sm text-muted-foreground">Total Students</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {students.reduce((sum, s) => sum + s.post_streak, 0)}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Streaks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {students.reduce((sum, s) => sum + s.badge_count, 0)}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Badges</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {students.reduce((sum, s) => sum + s.recent_posts, 0)}
                      </div>
                      <div className="text-sm text-muted-foreground">Recent Posts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Student Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {students.map((student) => (
                  <Card key={student.student_id}>
                    <CardHeader>
                      <CardTitle>{student.student_name || 'Student'}</CardTitle>
                      <CardDescription>
                        {student.last_activity_at
                          ? `Last active: ${new Date(student.last_activity_at).toLocaleDateString()}`
                          : 'No recent activity'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Streak Metrics */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Flame className="w-5 h-5 text-orange-500" />
                          <div>
                            <div className="text-2xl font-bold">{student.post_streak}</div>
                            <div className="text-xs text-muted-foreground">Current Streak</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-yellow-500" />
                          <div>
                            <div className="text-2xl font-bold">{student.longest_post_streak}</div>
                            <div className="text-xs text-muted-foreground">Best Streak</div>
                          </div>
                        </div>
                      </div>

                      {/* Badge Count */}
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-sm">
                          <Trophy className="w-3 h-3 mr-1" />
                          {student.badge_count} Badges
                        </Badge>
                      </div>

                      {/* Recent Posts */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MessageSquare className="w-4 h-4" />
                        <span>{student.recent_posts} posts in last 30 days</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
